import React, { useContext, useState, useEffect, useRef, FC } from "react";
import { Stepper, Group } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks"; // Import the hook for responsiveness
import { useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "@/contexts/UserContext/UserContext";
import ShipFormStep1 from "./ShipFormStep1";
import VesselTypeSelection from "./VesselTypeSelection";
import VesselSizeSelection from "./VesselSizeSelection";
import ShipFormSummary from "./ShipFormSummary";
import { Validatable } from "@/pages/study/StudyStepper";
import { getStepperStyles } from "@/helpers/getStepperStyles";
import { usePageTransition } from "@/hooks/helperHooks/usePageTransition";
import { useFetchProjects } from "@/hooks/apiHooks/useFetchProjects";
import { useAppColors } from "@/hooks/contextHooks/useAppColors";
import styles from "../../css/ship.module.css";

interface ShipFormSummaryRef extends Validatable {
  handleSubmit: (locked: boolean) => void;
}

const ShipStepper: FC = () => {
  const transition = usePageTransition();
  const [active, setActive] = useState(0);
  const context = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [highestStepVisited, setHighestStepVisited] = useState(active);
  const { colors, isDarkMode } = useAppColors();

  const isMobile = useMediaQuery("(max-width: 800px)");

  const { data: projects = [], isLoading: isLoadingProjects } =
    useFetchProjects();

  const shipInfoRef = useRef<Validatable | null>(null);
  const chooseShipTypeRef = useRef<Validatable | null>(null);
  const chooseShipSizeRef = useRef<Validatable | null>(null);
  const shipFormSummaryRef = useRef<ShipFormSummaryRef | null>(null);

  const handleStepChange = (nextStep: number) => {
    const isOutOfBounds = nextStep > 3 || nextStep < 0;
    if (isOutOfBounds) {
      return;
    }
    setActive(nextStep);
    setHighestStepVisited((hSC) => Math.max(hSC, nextStep));
  };

  const shouldAllowSelectStep = (step: number) =>
    highestStepVisited >= step && active !== step;

  const handleNext = () => {
    let isValid = true;

    if (active === 0 && shipInfoRef.current) {
      isValid = shipInfoRef.current.validate();
    } else if (active === 1 && chooseShipTypeRef.current) {
      isValid = chooseShipTypeRef.current.validate();
    } else if (active === 2 && chooseShipSizeRef.current) {
      isValid = chooseShipSizeRef.current.validate();
    } else if (active === 3 && shipFormSummaryRef.current) {
      isValid = shipFormSummaryRef.current.validate();
    }

    if (isValid) {
      handleStepChange(active + 1);
    }
  };

  const handleBack = () => {
    if (active === 0) {
      navigate("/");
    } else {
      handleStepChange(active - 1);
    }
  };

  useEffect(() => {
    if (!location.pathname.startsWith("/add-ship")) {
      console.log("Resetting form values");
      context?.setFormValues({});
    }
  }, [location, context]);

  const buttonContainerStyles: React.CSSProperties = {
    ...(isMobile && {
      flexDirection: "column",
      gap: "0.75rem",
      padding: "1rem",
    }),
  };

  const buttonStyles: React.CSSProperties = {
    ...(isMobile && {
      width: "100%",
    }),
  };

  if (!context || !context.formValues || transition || isLoadingProjects) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Stepper
        active={active}
        onStepClick={handleStepChange}
        style={{
          padding: isMobile ? "1rem 1.5rem" : "0 5vw",
          width: "100%",
        }}
        orientation={isMobile ? "vertical" : "horizontal"}
        styles={getStepperStyles(isDarkMode)}
      >
        <Stepper.Step
          label="General information"
          description="Ship Info"
          allowStepSelect={shouldAllowSelectStep(0)}
        >
          <ShipFormStep1 ref={shipInfoRef} projects={projects} />
        </Stepper.Step>
        <Stepper.Step
          label="Vessel Type"
          description="Select a vessel Type"
          allowStepSelect={shouldAllowSelectStep(1)}
        >
          <VesselTypeSelection ref={chooseShipTypeRef} />
        </Stepper.Step>
        <Stepper.Step
          label="Vessel Size"
          description="Select a vessel Size"
          allowStepSelect={shouldAllowSelectStep(2)}
        >
          <VesselSizeSelection ref={chooseShipSizeRef} />
        </Stepper.Step>
        <Stepper.Step
          label="General Particulars"
          description="Summary"
          allowStepSelect={shouldAllowSelectStep(3)}
        >
          <ShipFormSummary ref={shipFormSummaryRef} />
        </Stepper.Step>
        <Stepper.Completed>Completed</Stepper.Completed>
      </Stepper>

      <div className={styles.buttonContainer} style={buttonContainerStyles}>
        <div
          onClick={handleBack}
          className={styles.btn}
          style={{
            ...buttonStyles,
            backgroundColor: isDarkMode ? colors.secondary : colors.darkGray,
            color: colors.primary,
          }}
        >
          {active === 0 ? "Back to Home" : "Back"}
        </div>
        {active === 3 ? (
          <Group style={buttonStyles}>
            <div
              className={styles.btn}
              style={{
                ...buttonStyles,
                backgroundColor: isDarkMode ? colors.secondary : colors.brand,
                color: colors.primary,
              }}
              onClick={() => shipFormSummaryRef.current?.handleSubmit(false)}
            >
              Submit
            </div>
            <div
              className={styles.btn}
              style={{
                ...buttonStyles,
                backgroundColor: isDarkMode ? colors.secondary : colors.brand,
                color: colors.primary,
              }}
              onClick={() => shipFormSummaryRef.current?.handleSubmit(true)}
            >
              Submit & Lock
            </div>
          </Group>
        ) : (
          <div
            className={styles.btn}
            style={{
              ...buttonStyles,
              backgroundColor: isDarkMode ? colors.secondary : colors.brand,
              color: colors.primary,
            }}
            onClick={handleNext}
          >
            Next step
          </div>
        )}
      </div>
    </div>
  );
};

export default ShipStepper;
